import Products from '../../web-common/models/products/Products';
import FileReferenceDefinition from '../FileReferenceDefinition';

const definition: FileReferenceDefinition = {
  name: 'MacWebKitWebView',
  type: 'class',
  description: `MacWebKitWebView is the optional WebKit-based implementation of [IWebView](/webview/IWebView) in ${Products.standalone.markdownLink}. It's powered by macOS's built-in [WKWebView](https://developer.apple.com/documentation/webkit/wkwebview?language=objc) component and also includes additional APIs for WebKit-specific functionality. For details about the WebKit plugin and how to enable it, please see [this article](https://support.vuplex.com/articles/standalone-browser-engines).`,
  inheritsFrom: 'IWebView',
  additionalInterfaces: [
    'IWithDownloads',
    'IWithKeyDownAndUp',
    'IWithMovablePointer',
    'IWithNative2DMode',
    'IWithNativeJavaScriptDialogs',
    'IWithPdfCreation',
    'IWithPixelDensity',
    'IWithPointerDownAndUp',
    'IWithPopups',
    'IWithSettableUserAgent'
  ],
  sidebarSection: 'platform',
  members: [
    {
      name: 'PixelDensityEnabled',
      static: true,
      type: 'property',
      returns: 'bool',
      description: "By default, support for changing the webview's pixel density (via [WebViewPrefab.PixelDensity](https://developer.vuplex.com/webview/CanvasWebViewPrefab#PixelDensity) or [IWithPixelDensity](https://developer.vuplex.com/webview/IWithPixelDensity)) is disabled for the macOS WebKit plugin because it negatively impacts performance, particularly on Macs with Intel processors. However, if you wish to override this to enable changing the pixel density, you can do so by setting this field to `true`. The default is `false`.",
      example: `void Awake() {
    #if (UNITY_STANDALONE_OSX && !UNITY_EDITOR) || UNITY_EDITOR_OSX
        MacWebKitWebView.PixelDensityEnabled = true;
    #endif
}`
    },
    {
      name: 'GetNativeWebView',
      type: 'method',
      returns: 'IntPtr',
      description: "Returns an Objective-C pointer to the instance's underlying native [WKWebView](https://developer.apple.com/documentation/webkit/wkwebview?language=objc). The application can use this to utilize native macOS APIs for which 3D WebView doesn't yet have dedicated C# equivalents. To utilize the pointer, the application must pass it to a function defined in [native macOS library (.bundle file)](https://docs.unity3d.com/6000.2/Documentation/Manual/plug-ins-for-desktop.html) like illustrated in the example below.",
      warnings: ["Adding code that interacts with the native WKWebView directly may interfere with 3D WebView's functionality and vice versa. So, it's highly recommended to stick to 3D WebView's C# APIs whenever possible and only use GetNativeWebView() if truly necessary. If 3D WebView is missing an API that you need, feel free to [contact us](https://vuplex.com/contact)."],
      example: [
        {
          title: 'Objective-C Example',
          code: `// Example of defining a native Objective-C function that sets WKWebView.allowsLinkPreview.
// Compile this function into a .bundle library file and include it in your project.
#import <Foundation/Foundation.h>
#import <WebKit/WebKit.h>

void WebViewCustom_setAllowsLinkPreview(WKWebView *webView, BOOL allowsLinkPreview) {

    webView.allowsLinkPreview = allowsLinkPreview;
}`
        },
        {
          title: 'C# Example',
          code: `// Example of calling the native Objective-C function from C#.
async void EnableLinkPreviews(WebViewPrefab webViewPrefab) {

    await webViewPrefab.WaitUntilInitialized();
    #if (UNITY_STANDALONE_OSX && !UNITY_EDITOR) || UNITY_EDITOR_OSX
        var macWebView = webViewPrefab.WebView as MacWebKitWebView;
        var wkWebViewPtr = macWebView.GetNativeWebView();
        WebViewCustom_setAllowsLinkPreview(wkWebViewPtr, true);
    #endif
}

[System.Runtime.InteropServices.DllImport("YourPluginName.bundle")]
static extern void WebViewCustom_setAllowsLinkPreview(System.IntPtr webViewPtr, bool allowsLinkPreview);`
        }
      ]
    },
    {
      name: 'SetAllowFileAccessFromFileUrls',
      type: 'method',
      returns: 'void',
      description: "Sets whether cross-origin requests in the context of a file scheme URL should be allowed to access content from other file scheme URLs. The default value is `false`. Note that some accesses such as image HTML elements don't follow same-origin rules and aren't affected by this setting.",
      arguments: [
        {
          name: 'allow',
          type: 'bool'
        }
      ],
      example: `await webViewPrefab.WaitUntilInitialized();
#if (UNITY_STANDALONE_OSX && !UNITY_EDITOR) || UNITY_EDITOR_OSX
    var macWebView = webViewPrefab.WebView as MacWebKitWebView;
    macWebView.SetAllowFileAccessFromFileUrls(true);
#endif`
    },
    {
      name: 'SetAllowsBackForwardNavigationGestures',
      type: 'method',
      returns: 'void',
      arguments: [
        {
          name: 'allow',
          type: 'bool'
        }
      ],
      description: "When [Native 2D Mode](https://support.vuplex.com/articles/native-2d-mode) is enabled, this method sets whether horizontal swipe gestures trigger backward and forward page navigation. The default is `false`. When Native 2D Mode is disabled, this method has no effect.",
      example: `await canvasWebViewPrefab.WaitUntilInitialized();
#if (UNITY_STANDALONE_OSX && !UNITY_EDITOR) || UNITY_EDITOR_OSX
    var macWebView = canvasWebViewPrefab.WebView as MacWebKitWebView;
    macWebView.SetAllowsBackForwardNavigationGestures(true);
#endif`,
      seeAlso: '[WKWebView.allowsBackForwardNavigationGestures](https://developer.apple.com/documentation/webkit/wkwebview/1414995-allowsbackforwardnavigationgestu)'
    },
    {
      name: 'SetCameraEnabled',
      returns: 'void',
      static: true,
      type: 'method',
      arguments: [
        {
          type: 'bool',
          name: 'enabled'
        }
      ],
      description: "Like [Web.SetCameraAndMicrophoneEnabled()](/webview/Web#SetCameraAndMicrophoneEnabled), but enables only the camera without enabling the microphone. In addition to calling this method, you must also complete the additional steps described [here](https://support.vuplex.com/articles/webrtc) in order to successfully enable the camera.",
      example: `void Awake() {
    #if (UNITY_STANDALONE_OSX && !UNITY_EDITOR) || UNITY_EDITOR_OSX
        MacWebKitWebView.SetCameraEnabled(true);
    #endif
}`
    },
    {
      name: 'SetFullscreenEnabled',
      returns: 'void',
      type: 'method',
      arguments: [
        {
          type: 'bool',
          name: 'enabled'
        }
      ],
      description: "When [Native 2D Mode](https://support.vuplex.com/articles/native-2d-mode) is enabled, this method sets whether web pages can use the [JavaScript Fullscreen API](https://developer.mozilla.org/en-US/docs/Web/API/Fullscreen_API) to make an HTML element occupy the device's entire screen. The default is `true`, meaning that the JavaScript Fullscreen API is enabled by default. When Native 2D Mode is disabled, this method has no effect because the JavaScript Fullscreen API is only supported in Native 2D Mode.",
      example: `#if (UNITY_STANDALONE_OSX && !UNITY_EDITOR) || UNITY_EDITOR_OSX
    await canvasWebViewPrefab.WaitUntilInitialized();
    var macWebView = canvasWebViewPrefab.WebView as MacWebKitWebView;
    // Disable the JavaScript Fullscreen API.
    macWebView.SetFullscreenEnabled(false);
#endif`,
      seeAlso: '[Fullscreen support in 3D WebView](https://support.vuplex.com/articles/fullscreen)'
    },
    {
      name: 'SetMicrophoneEnabled',
      returns: 'void',
      static: true,
      type: 'method',
      arguments: [
        {
          type: 'bool',
          name: 'enabled'
        }
      ],
      description: "Like [Web.SetCameraAndMicrophoneEnabled()](/webview/Web#SetCameraAndMicrophoneEnabled), but enables only the microphone without enabling the camera. In addition to calling this method, you must also complete the additional steps described [here](https://support.vuplex.com/articles/webrtc) in order to successfully enable the microphone.",
      example: `void Awake() {
    #if (UNITY_STANDALONE_OSX && !UNITY_EDITOR) || UNITY_EDITOR_OSX
        MacWebKitWebView.SetMicrophoneEnabled(true);
    #endif
}`
    },
    {
      name: 'SetTargetFrameRate',
      type: 'method',
      returns: 'void',
      arguments: [
        {
          name: 'targetFrameRate',
          type: 'uint'
        }
      ],
      description: "Sets the target web frame rate. The default is `30`, which is also the maximum value. This method can be used to lower the target web frame rate in order to decrease energy and CPU usage. 3D WebView's rendering speed is limited by the speed of the underlying macOS APIs, so the actual web frame rate achieved is always lower than the default target of 30 FPS. This method is only used for the default render mode and is ignored when [Native 2D Mode](https://support.vuplex.com/articles/native-2d-mode) is enabled.",
      example: `await webViewPrefab.WaitUntilInitialized();
#if (UNITY_STANDALONE_OSX && !UNITY_EDITOR) || UNITY_EDITOR_OSX
    var macWebView = webViewPrefab.WebView as MacWebKitWebView;
    macWebView.SetTargetFrameRate(15);
#endif`,
    },
  ]
};
export default definition;

